import type { ManagerColor, OpportunityStatus } from "@salesboard/contracts";
import { cn } from "@/lib/utils";

/**
 * A stage rendered as a chip — the opportunities list's stage cell, and generic enough for
 * anywhere else a stage needs the same treatment.
 *
 * The chip's **text is the stage name**, never a parenthetical repeating what the name
 * already says ("Closed Won (Won)"). Tone is decoration on top of that: won reads success,
 * lost reads muted, open reads plain — colour is never the sole carrier of the outcome, per
 * [ui-patterns.md](../../../.claude/docs/ui-patterns.md#status-without-colour), because the
 * name itself is the non-colour cue.
 */
export function StageChip({
  name,
  closedType,
  color = null,
  className,
}: {
  name: string;
  closedType: OpportunityStatus;
  /**
   * The stage's own palette colour, configured on Settings. `null` — the launch state, and
   * whatever an administrator leaves unassigned — falls back to the tone `closedType` implies.
   */
  color?: ManagerColor | null;
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 font-medium",
        // Derived tone only where no colour is configured: three tones for however many stages
        // a team has is what made Discovery and Validation look identical.
        !color && closedType === "won" && "bg-success/15 text-success",
        !color && closedType === "lost" && "bg-muted text-muted-foreground",
        !color && closedType === "open" && "bg-muted/50 text-foreground",
        className,
      )}
      // A soft tint of the stage's colour with the theme's own text on top, not the badge's
      // full-strength fill: this sits inside a table row, where a saturated block would read
      // as a button. `color-mix` against the card token keeps it right in both themes.
      style={
        color
          ? {
              backgroundColor: `color-mix(in oklab, var(--manager-${color}) 22%, var(--card))`,
            }
          : undefined
      }
    >
      {name}
    </span>
  );
}
